import { useMemo } from "react";
import type { Activity } from "@/hooks/api/use-account-activities";
import type {
  MilestoneAnalysis,
  MilestoneAnalysisTouchpoint,
} from "@/hooks/api/use-milestone-analyses";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";

export interface ThreadConnectionMetadata {
  isInThread: boolean;
  isFirstInThread: boolean;
  isLastInThread: boolean;
  showThreadLine: boolean;
}

// Helper function to find and connect activities based on a matcher
const findAndConnectActivities = (
  activities: (Activity | CollapsedActivity)[],
  matcher: (activity: Activity | CollapsedActivity) => boolean,
): Map<string, ThreadConnectionMetadata> | null => {
  let firstMatchIndex: number | null = null;
  let lastMatchIndex: number | null = null;
  let matchCount = 0;
  const matchingActivityIds = new Set<string>(); // Keep track of specific IDs that matched

  activities.forEach((activity, index) => {
    if (matcher(activity)) {
      matchCount++;
      matchingActivityIds.add(activity.id);
      if (firstMatchIndex === null) {
        firstMatchIndex = index;
      }
      lastMatchIndex = index;
    }
  });

  if (firstMatchIndex !== null && lastMatchIndex !== null && matchCount > 1) {
    const connectionMap = new Map<string, ThreadConnectionMetadata>();
    activities.forEach((activity, index) => {
      const isMatch = matchingActivityIds.has(activity.id); // Use the set for accurate matching

      if (isMatch) {
        connectionMap.set(activity.id, {
          isInThread: true,
          isFirstInThread: index === firstMatchIndex,
          isLastInThread: index === lastMatchIndex,
          showThreadLine: false,
        });
      }

      // Check if this activity is between matched activities
      if (
        index > (firstMatchIndex as number) &&
        index < (lastMatchIndex as number) &&
        !isMatch // Ensure we only add thread lines for non-matching items in between
      ) {
        connectionMap.set(activity.id, {
          isInThread: false,
          isFirstInThread: false,
          isLastInThread: false,
          showThreadLine: true,
        });
      }
    });
    return connectionMap;
  }

  return null; // Return null if no connection should be made
};

export function useThreadMetadata(
  selectedActivity: Activity | CollapsedActivity | null,
  activities: (Activity | CollapsedActivity)[],
  selectedMilestoneAnalysis?: MilestoneAnalysis,
) {
  const threadConnectionMap = useMemo(() => {
    if (!activities.length) {
      return new Map<string, ThreadConnectionMetadata>();
    }

    // 1. Milestone Analysis Evidence (Highest Priority)
    if (selectedMilestoneAnalysis) {
      const evidence = selectedMilestoneAnalysis?.touchpoint_citations || [];
      if (evidence.length > 0) {
        const evidenceIds = new Set(
          evidence.map(
            (ev: MilestoneAnalysisTouchpoint) => ev.id || ev.inferred_from_id,
          ),
        );
        const connectedActivityIds = new Set<string>();

        // Find all activities (including sub-activities) that match evidence
        activities.forEach((activity) => {
          if (evidenceIds.has(activity.id)) {
            connectedActivityIds.add(activity.id);
          }
          if ("activities" in activity) {
            activity.activities.forEach((subActivity) => {
              if (evidenceIds.has(subActivity.id)) {
                // If a sub-activity matches, connect the parent group as well
                connectedActivityIds.add(activity.id);
              }
            });
          }
        });

        // Use the helper function with a specific matcher for milestone analysis
        const milestoneAnalysisMatcher = (
          activity: Activity | CollapsedActivity,
        ) => connectedActivityIds.has(activity.id);

        const milestoneAnalysisConnections = findAndConnectActivities(
          activities,
          milestoneAnalysisMatcher,
        );

        if (milestoneAnalysisConnections) {
          return milestoneAnalysisConnections;
        }
      }
    }

    // If no milestone analysis connection or no selected activity, return empty map early
    if (!selectedActivity) {
      return new Map<string, ThreadConnectionMetadata>();
    }

    // Extract identifiers from the selected activity
    const selectedThreadId = selectedActivity.thread_id;
    const selectedCampaignId = selectedActivity.campaign_id;
    const selectedSessionId = selectedActivity.session_id;
    const selectedActivityDescription = selectedActivity.activity;

    // 2. Thread ID Connection
    if (selectedThreadId) {
      const threadMatcher = (activity: Activity | CollapsedActivity) =>
        activity.record_type === "direct_email" &&
        activity.thread_id === selectedThreadId;
      const threadConnections = findAndConnectActivities(
        activities,
        threadMatcher,
      );
      if (threadConnections) return threadConnections;
    }

    // 3. Campaign ID Connection
    if (selectedCampaignId) {
      const campaignMatcher = (activity: Activity | CollapsedActivity) =>
        activity.campaign_id === selectedCampaignId;
      const campaignConnections = findAndConnectActivities(
        activities,
        campaignMatcher,
      );
      if (campaignConnections) return campaignConnections;
    }

    // 4. Session ID Connection
    if (selectedSessionId) {
      const sessionMatcher = (activity: Activity | CollapsedActivity) =>
        activity.session_id === selectedSessionId;
      const sessionConnections = findAndConnectActivities(
        activities,
        sessionMatcher,
      );
      if (sessionConnections) return sessionConnections;
    }

    // 5. Activity Description Connection (Fallback)
    if (selectedActivityDescription) {
      const descriptionMatcher = (activity: Activity | CollapsedActivity) =>
        activity.activity === selectedActivityDescription;
      const descriptionConnections = findAndConnectActivities(
        activities,
        descriptionMatcher,
      );
      if (descriptionConnections) return descriptionConnections;
    }

    // Default: No connections found
    return new Map<string, ThreadConnectionMetadata>();
  }, [selectedActivity, activities, selectedMilestoneAnalysis]);

  const getThreadInfo = useMemo(() => {
    return (
      activity: Activity | CollapsedActivity,
      isExpanded = false,
    ): ThreadConnectionMetadata => {
      const metadata = threadConnectionMap.get(activity.id);

      // If this is a regular activity in an expanded group
      if (
        !("activities" in activity) &&
        metadata?.isFirstInThread &&
        isExpanded
      ) {
        return {
          isInThread: metadata.isInThread,
          isFirstInThread: false,
          isLastInThread: metadata.isLastInThread,
          showThreadLine: metadata.showThreadLine,
        };
      }

      // If this is a group
      if ("activities" in activity) {
        const hasFirstInThread = activity.activities.some((subActivity) => {
          const subMetadata = threadConnectionMap.get(subActivity.id);
          return subMetadata?.isFirstInThread;
        });
        const hasLastInThread = activity.activities.some((subActivity) => {
          const subMetadata = threadConnectionMap.get(subActivity.id);
          return subMetadata?.isLastInThread;
        });
        const hasInThread = activity.activities.some((subActivity) => {
          const subMetadata = threadConnectionMap.get(subActivity.id);
          return subMetadata?.isInThread;
        });
        const hasShowThreadLine = activity.activities.some((subActivity) => {
          const subMetadata = threadConnectionMap.get(subActivity.id);
          return subMetadata?.showThreadLine;
        });

        if (isExpanded && hasFirstInThread) {
          return {
            isInThread: true,
            isFirstInThread: true,
            isLastInThread: false,
            showThreadLine: false,
          };
        } else if (!isExpanded && hasFirstInThread && hasLastInThread) {
          return {
            isInThread: false,
            isFirstInThread: false,
            isLastInThread: false,
            showThreadLine: false,
          };
        } else if (!isExpanded && hasLastInThread) {
          // If collapsed and contains the last item, show the last item state,
          // using hasInThread/hasShowThreadLine if available, otherwise fallback to metadata/defaults.
          // CRITICAL FIX: The last item should never show a thread line passing through it.
          return {
            isInThread: hasInThread ?? metadata?.isInThread ?? false,
            isFirstInThread: hasFirstInThread, // If hasLastInThread is true, this must be false here
            isLastInThread: true, // We know this is true
            showThreadLine: false, // Corrected: Always false for the last item
          };
        } else if (!isExpanded && hasFirstInThread) {
          return {
            isInThread: hasInThread ?? metadata?.isInThread ?? true,
            isFirstInThread: hasFirstInThread ?? true,
            isLastInThread:
              hasLastInThread ?? metadata?.isLastInThread ?? false,
            showThreadLine: false,
          };
        } else if (isExpanded && hasLastInThread) {
          return {
            isInThread: true,
            isFirstInThread: false,
            isLastInThread: false,
            showThreadLine: true,
          };
        } else if (hasInThread) {
          return {
            isInThread: true,
            isFirstInThread: false,
            isLastInThread: false,
            showThreadLine: false,
          };
        } else if (hasShowThreadLine) {
          return {
            isInThread: false,
            isFirstInThread: false,
            isLastInThread: false,
            showThreadLine: true,
          };
        }
      }

      return (
        metadata ?? {
          isInThread: false,
          isFirstInThread: false,
          isLastInThread: false,
          showThreadLine: false,
        }
      );
    };
  }, [threadConnectionMap]);

  return {
    threadConnectionMap,
    getThreadInfo,
  };
}
